import {
  IServerSideDatasource,
  IServerSideGetRowsParams,
  SortModelItem
} from 'ag-grid-community';
import { isEmpty } from 'lodash';
import { useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { HeaderData } from 'adapters/types';
import { DEFAULT_PAGE_NO, Tabs } from 'common/constants';
import { useClient } from 'components/ClientProvider';
import { getCompanyId, getStoreId } from 'components/StoreSelect/slice';
import { onErrorHandler } from 'hooks/helper';
import { getSelectedTab } from 'pages/LandingPage/components/LandingPageHeader/slice';
import { getVendorsTableDataId } from 'pages/VendorsTablePage/components/VendorsTable/helpers';
import {
  getDebouncedSearchInputValue,
  getDueDate,
  getPaymentMethods,
  getPrevSortColumn,
  getPrevSortOrder,
  setHighVolumeColumnState,
  setHighVolumeFilterData,
  setPrevSortColumn,
  setPrevSortOrder,
  setVendorsResponse
} from 'pages/VendorsTablePage/components/VendorsTable/slice';
import { VendorsTableDataType } from 'pages/VendorsTablePage/components/VendorsTable/types';
import { errors } from 'pages/VendorsTablePage/messages';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  HIGH_VOLUME_VENDORS_PAGE_SIZE,
  SORT_COLUMN_PARAMS,
  SORT_ORDER_PARAMS
} from '../constants';
import { getSortedInvoiceRows } from '../utils';

export const useVendorsServerSideDatasource = (): {
  isFilterChanged: boolean;
  serverSideDatasource: IServerSideDatasource;
} => {
  const intl = useIntl();
  const client = useClient();
  const dispatch = useAppDispatch();
  const prevSortColumn = useAppSelector(getPrevSortColumn);
  const prevSortOrder = useAppSelector(getPrevSortOrder);
  const selectedPaymentMethods = useAppSelector(getPaymentMethods);
  const debouncedSearchValue = useAppSelector(getDebouncedSearchInputValue);
  const dueDate = useAppSelector(getDueDate);
  const companyId = useAppSelector(getCompanyId);
  const storeId = useAppSelector(getStoreId);
  const selectedTab = useAppSelector(getSelectedTab);
  const paymentMethod =
    selectedPaymentMethods?.length > 1 ? undefined : selectedPaymentMethods[0];
  const isFilterChangedRef = useRef(true);
  const filterData = useMemo(() => {
    isFilterChangedRef.current = true;
    return {
      storeId,
      companyId,
      debouncedSearchValue,
      dueDate,
      paymentMethod,
      selectedTab
    };
  }, [
    companyId,
    debouncedSearchValue,
    dueDate,
    paymentMethod,
    selectedTab,
    storeId
  ]);

  const serverSideDatasource = useMemo(
    () => ({
      getRows: async ({
        api,
        request,
        success,
        fail
      }: IServerSideGetRowsParams<VendorsTableDataType>) => {
        const handleGroupedRows = (sortColumnData: SortModelItem) => {
          const node = api.getRowNode(request.groupKeys[0]);
          if (node?.data === undefined) return fail();
          const nodeData = node.data;
          const { invoices, ...rest } = nodeData;
          const rowData: VendorsTableDataType[] = invoices.map(
            (invoice: any) => ({
              ...rest,
              ...invoice,
              id: getVendorsTableDataId(nodeData, invoice),
              invoices: []
            })
          );
          const sortedRowData =
            sortColumnData !== undefined
              ? [...rowData].sort(getSortedInvoiceRows(sortColumnData))
              : rowData;
          return success({ rowData: sortedRowData });
        };

        function getSortColumns(sortColumnData: SortModelItem) {
          const sortColumn = sortColumnData
            ? SORT_COLUMN_PARAMS[sortColumnData.colId]
            : undefined;
          const sortOrder = sortColumnData
            ? SORT_ORDER_PARAMS[sortColumnData.sort]
            : undefined;
          const sortChanged =
            sortColumn !== prevSortColumn || sortOrder !== prevSortOrder;
          return { sortChanged, sortColumn, sortOrder };
        }

        const fetchAndProcessVendors = async (
          sortColumnData: SortModelItem
        ) => {
          const headers: HeaderData = {
            ...client?.headers
          };
          const page = request.startRow
            ? Math.round(request.startRow / HIGH_VOLUME_VENDORS_PAGE_SIZE) + 1
            : DEFAULT_PAGE_NO;
          const { sortChanged, sortColumn, sortOrder } =
            getSortColumns(sortColumnData);
          if (isFilterChangedRef.current || sortChanged) {
            api.showLoadingOverlay();
          }
          try {
            const result = await client?.data.fetchHighVolumeVendors(
              headers,
              filterData.companyId,
              filterData.storeId,
              page,
              HIGH_VOLUME_VENDORS_PAGE_SIZE,
              filterData.dueDate,
              filterData.paymentMethod,
              filterData.selectedTab,
              filterData.debouncedSearchValue,
              sortColumn,
              sortOrder
            );
            const rowData: VendorsTableDataType[] =
              result?.vendors.map(vendorData => ({
                ...vendorData,
                id: vendorData.vendorNo,
                amount: vendorData.totalAmount,
                discountAmount: vendorData.totalDiscountAmount,
                netAmount: vendorData.totalNetAmount,
                onHold: vendorData.onHold ?? false,
                vendorOnHold: vendorData.onHold ?? false,
                invoiceDescription: '',
                invoiceId: ''
              })) ?? [];
            if (result) dispatch(setVendorsResponse(result));
            success({ rowData });
            api.hideOverlay();
            isFilterChangedRef.current = false;
            dispatch(setPrevSortColumn(sortColumn));
            dispatch(setPrevSortOrder(sortOrder));
            return api
              .getState()
              ?.rowGroupExpansion?.expandedRowGroupIds.forEach((id: any) => {
                const rowNode = api.getRowNode(id);
                if (rowNode) {
                  api.setRowNodeExpanded(rowNode, true);
                }
              });
          } catch (err: any) {
            onErrorHandler(
              err,
              dispatch,
              intl,
              intl.formatMessage(errors.vendorApiFailureMsg)
            );
            return fail();
          }
        };

        if (isEmpty(client?.headers['x-department-name'])) {
          return success({ rowData: [] });
        }
        const sortColumnData = request.sortModel[0];

        dispatch(setHighVolumeFilterData(filterData));
        dispatch(setHighVolumeColumnState(sortColumnData));

        if (request.groupKeys.length > 0) {
          return handleGroupedRows(sortColumnData);
        }
        if (
          filterData.selectedTab === Tabs.NeedsAuthorization &&
          !filterData.paymentMethod
        ) {
          return fail();
        }
        return fetchAndProcessVendors(sortColumnData);
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client?.data, client?.headers, dispatch, filterData, intl]
  );
  return useMemo(
    () => ({
      isFilterChanged: isFilterChangedRef.current,
      serverSideDatasource
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serverSideDatasource, isFilterChangedRef.current]
  );
};
